import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useArchiveOrganizationLabel,
  useCreateOrganizationLabel,
  useOrganizationLabels,
  useUpdateOrganizationLabel,
} from "@/hooks/useOrganizationLabels";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { toTitleCase } from "@/utils/toTitleCase";

import {
  OrganizationLabel,
  OrganizationLabelPayload,
} from "@/domain/organizationLabels.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import OrganizationLabelFilters, {
  Filters,
} from "@/ui/content/organizationLabels/Filters";
import OrganizationLabelModal from "@/ui/modals/organizationLabels/OrganizationLabelModal";

const DEFAULT_FILTERS: Filters = {
  sortField: "name",
  sortOrder: 1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  status: "",
};

const OrganizationLabelsPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    organizationLabel?: OrganizationLabel;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    organizationLabel?: OrganizationLabel;
  }>({ open: false });

  const {
    organizationLabels,
    isLoadingOrganizationLabels,
    isFetchingOrganizationLabels,
    refetchOrganizationLabels,
  } = useOrganizationLabels({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: debouncedSearch,
    status: filters.status,
  });

  const createLabel = useCreateOrganizationLabel();
  const updateLabel = useUpdateOrganizationLabel();
  const archiveLabel = useArchiveOrganizationLabel();
  const createAuditLog = useCreateAuditlog();

  const isSaving = createLabel.isPending || updateLabel.isPending;

  useEffect(() => {
    setPageTitle("Organization Labels");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.ORGANIZATION_LABELS,
    option: OPTIONS.ORGANIZATION_LABELS_LIST,
    actionCode: ACTION_CODE.WEB_ORG_LABEL_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of organization labels",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchOrganizationLabels();
  }, [
    debouncedSearch,
    filters.status,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ORGANIZATION_LABELS,
        option: OPTIONS.ORGANIZATION_LABELS_LIST,
        actionCode: ACTION_CODE.WEB_ORG_LABEL_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of organization labels",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, organizationLabel: undefined });
  };

  const handleOpenEdit = (organizationLabel: OrganizationLabel) => {
    setModalState({ open: true, organizationLabel });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, organizationLabel: undefined });
  };

  const handleSave = async (data: OrganizationLabelPayload) => {
    const editing = modalState.organizationLabel;

    try {
      if (editing) {
        await updateLabel.mutateAsync({
          id: editing._id,
          payload: data,
        });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ORGANIZATION_LABELS,
          option: OPTIONS.ORGANIZATION_LABEL_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_ORG_LABEL_EDIT,
          action: "When editing an organization label",
          detail: `Edited organization label ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The organization label has been updated.", "success");
      } else {
        await createLabel.mutateAsync(data);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ORGANIZATION_LABELS,
          option: OPTIONS.ORGANIZATION_LABEL_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_ORG_LABEL_ADD,
          action: "When creating an organization label",
          detail: `Created organization label ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The organization label has been created.", "success");
      }

      handleCloseModal();
      refetchOrganizationLabels();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.organizationLabel;
    if (!target) return;

    try {
      await archiveLabel.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ORGANIZATION_LABELS,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_ORG_LABEL_DELETE,
        action: "When deleting an organization label",
        detail: `Deleted organization label ${target.name}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchOrganizationLabels();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "220px",
    },
    {
      id: "description",
      isSortable: false,
      disablePadding: false,
      label: "Description",
      align: "left",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "140px",
    },
    {
      id: "createdAt",
      isSortable: true,
      disablePadding: false,
      label: "Created",
      align: "left",
      width: "160px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Action",
      align: "right",
      width: "100px",
    },
  ];

  const rows = useMemo(() => {
    if (!organizationLabels || organizationLabels.list.length === 0) return [];

    return organizationLabels.list.map((label, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(label),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () =>
            setDeleteAlert({ open: true, organizationLabel: label }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      return createDataRow(index, {
        name: <div>{label.name}</div>,
        description: <div>{label.description || "-"}</div>,
        status: (
          <Chip
            sx={{
              bgcolor:
                label.status === "ACTIVE"
                  ? theme.palette.brand.light
                  : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                label.status === "ACTIVE"
                  ? theme.palette.brand.main
                  : theme.palette.warning.dark,
            }}
            label={<div className="text-xs">{toTitleCase(label.status)}</div>}
          />
        ),
        createdAt: (
          <div>
            {label.createdAt
              ? dayjs(label.createdAt).format("DD MMM YYYY")
              : "-"}
          </div>
        ),
        action: <ActionMenu id={label._id} options={actionMenu} />,
      });
    });
  }, [organizationLabels, theme]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Organization Labels
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <OrganizationLabelFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={organizationLabels?.totalCount}
                  isLoading={
                    isLoadingOrganizationLabels || isFetchingOrganizationLabels
                  }
                  messageEmptyData="No organization labels to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({ ...prev, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <OrganizationLabelModal
          open={modalState.open}
          onClose={handleCloseModal}
          organizationLabel={modalState.organizationLabel}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveLabel.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default OrganizationLabelsPage;
